import { type Editor } from '@tiptap/react';

import { getDynamicFieldLabel, getLayoutFields } from './utils';

import FileInput from '@/components/FileInput';
import RichTextField from '@/components/RichTextField';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

interface DynamicFieldsProps {
  defaultImage?: null | string;
  defaultImage2?: null | string;
  defaultTextHeader1?: string;
  defaultTextHeader2?: string;
  defaultTextHeader3?: string;
  layoutTypeValue: string;
  text2FieldRef: React.RefObject<Editor | null>;
  text3FieldRef: React.RefObject<Editor | null>;
  text4FieldRef: React.RefObject<Editor | null>;
  textFieldRef: React.RefObject<Editor | null>;
}

const DynamicFields = ({
  defaultImage,
  defaultImage2,
  defaultTextHeader1,
  defaultTextHeader2,
  defaultTextHeader3,
  layoutTypeValue,
  text2FieldRef,
  text3FieldRef,
  text4FieldRef,
  textFieldRef
}: DynamicFieldsProps) => {
  const dynamicFields = getLayoutFields(layoutTypeValue);
  const imageFields = dynamicFields.filter(
    field => field === 'image' || field === 'image_2'
  );
  const textHeaderFields = dynamicFields.filter(
    field =>
      field === 'text_header_1' ||
      field === 'text_header_2' ||
      field === 'text_header_3'
  );
  const otherFields = dynamicFields.filter(
    field =>
      field !== 'image' &&
      field !== 'image_2' &&
      field !== 'text_header_1' &&
      field !== 'text_header_2' &&
      field !== 'text_header_3'
  );

  return (
    <>
      {textHeaderFields.length > 0 && (
        <div className="flex gap-x-2.5">
          {textHeaderFields.map(dynamicField => {
            const label = getDynamicFieldLabel(dynamicField);
            const name =
              dynamicField === 'text_header_1'
                ? 'textHeader1'
                : dynamicField === 'text_header_2'
                  ? 'textHeader2'
                  : 'textHeader3';

            const defaultValue =
              dynamicField === 'text_header_1'
                ? defaultTextHeader1
                : dynamicField === 'text_header_2'
                  ? defaultTextHeader2
                  : defaultTextHeader3;

            return (
              <Field
                className="flex-1"
                key={dynamicField}
              >
                <FieldLabel htmlFor={`${name}-field`}>{label}</FieldLabel>
                <Input
                  defaultValue={defaultValue}
                  id={`${name}-field`}
                  name={name}
                  placeholder="Enter Value"
                />
              </Field>
            );
          })}
        </div>
      )}

      {imageFields.length > 0 && (
        <div className="flex gap-x-2.5">
          {imageFields.map(dynamicField => {
            const label = getDynamicFieldLabel(dynamicField);
            const name = dynamicField === 'image' ? 'image' : 'image2';
            const existingData =
              dynamicField === 'image' ? defaultImage : defaultImage2;

            return (
              <Field
                className="flex-1"
                key={dynamicField}
              >
                <FieldLabel htmlFor={`${name}-field`}>{label}</FieldLabel>
                <FileInput
                  accept="image/*"
                  data={existingData}
                  id={`${name}-field`}
                  name={name}
                />
              </Field>
            );
          })}
        </div>
      )}

      {otherFields.map(dynamicField => {
        const label = getDynamicFieldLabel(dynamicField);

        switch (dynamicField) {
          case 'text':
            return (
              <Field key={dynamicField}>
                <FieldLabel htmlFor="text-field">{label}</FieldLabel>
                <RichTextField
                  id="text-field"
                  ref={textFieldRef}
                />
              </Field>
            );
          case 'text_2':
            return (
              <Field key={dynamicField}>
                <FieldLabel htmlFor="text2-field">{label}</FieldLabel>
                <RichTextField
                  id="text2-field"
                  ref={text2FieldRef}
                />
              </Field>
            );
          case 'text_3':
            return (
              <Field key={dynamicField}>
                <FieldLabel htmlFor="text3-field">{label}</FieldLabel>
                <RichTextField
                  id="text3-field"
                  ref={text3FieldRef}
                />
              </Field>
            );
          case 'text_4':
            return (
              <Field key={dynamicField}>
                <FieldLabel htmlFor="text4-field">{label}</FieldLabel>
                <RichTextField
                  id="text4-field"
                  ref={text4FieldRef}
                />
              </Field>
            );
          default:
            return null;
        }
      })}
    </>
  );
};

export default DynamicFields;
